import type { TrialStatusModel } from "src/app/Services/subscription.service";

/** Starter free trial snapshot, refreshed from GET api/subscriptions/trial-status. */
export const TRIAL_STATE_STORAGE_KEY = "flowchestTrialState";

export const TRIAL_DEFAULT_DAYS = 14;
export const TRIAL_YEARLY_DISCOUNT_PERCENT = 20;

export function getTrialState(): TrialStatusModel | null {
  try {
    const raw = localStorage.getItem(TRIAL_STATE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as TrialStatusModel) : null;
  } catch {
    return null;
  }
}

export function setTrialState(state: TrialStatusModel | null): void {
  try {
    if (state) {
      localStorage.setItem(TRIAL_STATE_STORAGE_KEY, JSON.stringify(state));
    } else {
      localStorage.removeItem(TRIAL_STATE_STORAGE_KEY);
    }
  } catch {
    // Storage unavailable (private mode); trial gating falls back to API errors.
  }
}

/**
 * Trial limits apply to personal work only; org members work under the organization's plan.
 */
export function isTrialActive(): boolean {
  try {
    if (
      localStorage.getItem("accountUsageMode") === "organization" &&
      localStorage.getItem("activeOrganizationId")
    ) {
      return false;
    }
  } catch {
    return false;
  }
  return getTrialState()?.active === true;
}

export function isTrialEligible(): boolean {
  const state = getTrialState();
  return state ? state.eligible === true : true;
}

export function getTrialDaysLeft(): number {
  return Math.max(0, getTrialState()?.daysLeft ?? 0);
}

export function trialAllowsAi(): boolean {
  return !isTrialActive() || getTrialState()?.allowAi === true;
}

export function trialAllowsGuidePacks(): boolean {
  return !isTrialActive() || getTrialState()?.allowGuidePacks === true;
}

/** True when the trial guide allowance is used up (the API is the source of truth). */
export function isTrialGuideLimitReached(): boolean {
  if (!isTrialActive()) return false;
  const state = getTrialState();
  return !!state && state.guidesUsed >= state.maxGuides;
}

export const PAYPAL_AUTOPAY_URL = "https://www.paypal.com/myaccount/autopay/";
const PAYMENT_FAILED_DISMISS_KEY = "flowchestPaymentFailedDismissed";

/** The user's last subscription ended because a charge failed and they haven't dismissed the notice. */
export function getUndismissedPaymentFailure(): TrialStatusModel | null {
  const state = getTrialState();
  if (!state?.lastPaymentFailedUtc) return null;
  try {
    if (localStorage.getItem(PAYMENT_FAILED_DISMISS_KEY) === (state.lastPaymentFailedSubscriptionId || state.lastPaymentFailedUtc)) {
      return null;
    }
  } catch {
    // Storage unavailable: show the notice.
  }
  return state;
}

export function dismissPaymentFailure(): void {
  const state = getTrialState();
  if (!state?.lastPaymentFailedUtc) return;
  try {
    localStorage.setItem(
      PAYMENT_FAILED_DISMISS_KEY,
      state.lastPaymentFailedSubscriptionId || state.lastPaymentFailedUtc
    );
  } catch {
    // Ignore; the notice will show again next session.
  }
}

export function formatTrialEndDate(state: TrialStatusModel | null = getTrialState()): string {
  if (!state?.trialEndsUtc) return "";
  const date = new Date(state.trialEndsUtc);
  return isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
